import React, { useEffect, useMemo, useState } from 'react';
import {
  ChevronLeft, ChevronRight, ChevronDown, CalendarDays, Plane, Ship,
  FileText, Users, Package, BarChart3, IndianRupee, TrendingUp
} from 'lucide-react';
import {
  aggregateByDay, buildMonthCalendar, formatCurrency, formatShortCurrency, toDateKey
} from '../../utils/fobAggregation';
import './FobCalendar.css';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

// A day's dot colour, so a month reads as a pattern rather than a wall of blue.
const DOTS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#ef4444'];
const dotFor = (key) => DOTS[[...key].reduce((a, c) => a + c.charCodeAt(0), 0) % DOTS.length];

const SECONDARY = [
  { key: 'rodtep', label: 'RodTEP' },
  { key: 'dbk', label: 'DBK' },
  { key: 'igst', label: 'IGST' },
  { key: 'rosctl', label: 'ROSCTL' },
];

const shortDay = (d) => `${d.getDate()} ${MONTHS[d.getMonth()].slice(0, 3)}`;
const plural = (n, w) => `${n} ${w}${n === 1 ? '' : 's'}`;

// The month to open on: this one if it has shipments, else the latest that does.
const initialMonth = (dayMap) => {
  const now = new Date();
  const prefix = toDateKey(now).slice(0, 7);
  const keys = Object.keys(dayMap).sort();
  if (!keys.length || keys.some((k) => k.startsWith(prefix))) {
    return { year: now.getFullYear(), monthIndex: now.getMonth() };
  }
  const latest = dayMap[keys[keys.length - 1]].date;
  return { year: latest.getFullYear(), monthIndex: latest.getMonth() };
};

const Spark = ({ values, colour }) => {
  const max = Math.max(...values, 1);
  return (
    <span className="fcal__spark" aria-hidden="true">
      {values.map((v, i) => (
        <i key={i} style={{ height: `${Math.max(14, (v / max) * 100)}%`, background: colour, opacity: 0.45 + (v / max) * 0.55 }} />
      ))}
    </span>
  );
};

// The short form fits the card at a size worth reading; the exact figure is
// a hover away.
const SummaryCard = ({ icon: Icon, tint, label, value, title, delta }) => (
  <div className="fcal__sumcard">
    <span className="fcal__sumicon" style={{ background: tint.bg, color: tint.fg }}><Icon size={18} /></span>
    <span className="min-w-0">
      <span className="fcal__sumlabel block">{label}</span>
      <span className="fcal__sumvalue block truncate" title={title}>{value}</span>
    </span>
    {delta !== undefined && (
      <span className="fcal__delta"><TrendingUp size={10} />{delta}%</span>
    )}
  </div>
);

/**
 * Month calendar of FOB value by S/B date.
 * `items` are shipments normalised with `normalizeShipments`.
 */
const FobCalendar = ({ items = [], loading = false, transportType = 'air' }) => {
  const dayMap = useMemo(() => aggregateByDay(items), [items]);
  const [view, setView] = useState(() => initialMonth(dayMap));
  const [openKey, setOpenKey] = useState(null);
  const [openWeek, setOpenWeek] = useState(null);
  const [ready, setReady] = useState(items.length > 0);

  useEffect(() => {
    if (!ready && items.length > 0) { setView(initialMonth(dayMap)); setReady(true); }
  }, [ready, items.length, dayMap]);

  const calendar = useMemo(
    () => buildMonthCalendar(null, view.year, view.monthIndex, dayMap),
    [dayMap, view]
  );

  const now = new Date();
  const todayKey = toDateKey(now);
  const isThisMonth = view.year === now.getFullYear() && view.monthIndex === now.getMonth();
  const Mode = transportType === 'sea' ? Ship : Plane;

  // Distinct buyers across the month — "clients" in the summary.
  const clients = useMemo(() => {
    const seen = new Set();
    calendar.weeks.forEach((w) => w.days.forEach((cell) => {
      cell?.data?.shipments?.forEach((sh) => { if (sh.buyer) seen.add(String(sh.buyer).trim().toUpperCase()); });
    }));
    return seen.size;
  }, [calendar]);

  const move = (delta) => {
    setOpenKey(null);
    setOpenWeek(null);
    setView(({ year, monthIndex }) => {
      const d = new Date(year, monthIndex + delta, 1);
      return { year: d.getFullYear(), monthIndex: d.getMonth() };
    });
  };

  const totals = calendar.totals;
  const avgPerShipment = totals.count ? totals.fob / totals.count : 0;

  if (loading) {
    return (
      <div className="fcal__empty">
        <span className="inline-block w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin align-middle" />
        <span className="ml-3 align-middle">Loading shipments…</span>
      </div>
    );
  }

  return (
    <div className="fcal">
      {/* ── toolbar ─────────────────────────────────────────────────────── */}
      <div className="fcal__bar">
        <div className="fcal__nav">
          <button type="button" className="fcal__arrow" onClick={() => move(-1)} aria-label="Previous month">
            <ChevronLeft size={18} />
          </button>
          <span className="fcal__month">
            <CalendarDays size={17} className="text-blue-500" />
            {MONTHS[view.monthIndex]} {view.year}
          </span>
          <button type="button" className="fcal__arrow" onClick={() => move(1)} aria-label="Next month">
            <ChevronRight size={18} />
          </button>
          <button
            type="button"
            className="fcal__thismonth"
            disabled={isThisMonth}
            onClick={() => { setOpenKey(null); setOpenWeek(null); setView({ year: now.getFullYear(), monthIndex: now.getMonth() }); }}
          >
            This Month <ChevronDown size={14} />
          </button>
        </div>

      </div>

      {/* ── grid + week rail ────────────────────────────────────────────── */}
      <div className="fcal__body">
        <div className="fcal__scroll">
          <div className="fcal__grid">
            {WEEKDAYS.map((d) => <div key={d} className="fcal__weekday">{d}</div>)}

            {calendar.weeks.map((week) => week.days.map((cell, i) => {
              if (!cell) {
                const dim = openWeek !== null && openWeek !== week.index;
                return <div key={`e-${week.index}-${i}`} className={`fcal__cell fcal__cell--outside${dim ? ' fcal__cell--dim' : ''}`} />;
              }
              const { data, dateKey, day } = cell;
              const open = openKey === dateKey;
              const inOpenWeek = openWeek !== null && openWeek === week.index;
              const cls = [
                'fcal__cell',
                data ? 'fcal__cell--has' : '',
                dateKey === todayKey ? 'fcal__cell--today' : '',
                open ? 'fcal__cell--open' : '',
                inOpenWeek ? 'fcal__cell--inweek' : '',
                openWeek !== null && !inOpenWeek ? 'fcal__cell--dim' : '',
              ].filter(Boolean).join(' ');

              return (
                <div
                  key={dateKey}
                  className={cls}
                  onClick={data ? () => setOpenKey(open ? null : dateKey) : undefined}
                  role={data ? 'button' : undefined}
                  tabIndex={data ? 0 : undefined}
                  onKeyDown={data ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpenKey(open ? null : dateKey); } } : undefined}
                >
                  <span className="fcal__daynum">{day}</span>

                  {data && (
                    <span className={`fcal__badge${open ? '' : ' fcal__badge--doc'}`}>
                      {open ? <Mode size={12} /> : <FileText size={12} />}
                    </span>
                  )}

                  {data && (
                    <>
                      <span className="fcal__count">
                        <i className="fcal__dot" style={{ background: dotFor(dateKey) }} />
                        {plural(data.count, 'shipment')}
                      </span>
                      <span className="fcal__fob" title={formatCurrency(data.fob)}>{formatShortCurrency(data.fob)}</span>

                      {open && (
                        <span className="fcal__extra">
                          {SECONDARY.filter(({ key }) => data[key] > 0).map(({ key, label }) => (
                            <div key={key}><span>{label}</span><b>{formatShortCurrency(data[key])}</b></div>
                          ))}
                        </span>
                      )}
                    </>
                  )}
                </div>
              );
            }))}
          </div>
        </div>

        <div className="fcal__weeks">
          {calendar.weeks.map((week, idx) => {
            const days = week.days.filter(Boolean);
            if (!days.length) return null;
            const from = days[0].date;
            const to = days[days.length - 1].date;
            const bars = days.map((d) => d.data?.fob || 0);
            const colour = DOTS[idx % DOTS.length];
            const open = openWeek === week.index;
            return (
              <button
                key={week.index}
                type="button"
                className={`fcal__week${open ? ' is-open' : ''}`}
                onClick={() => setOpenWeek(open ? null : week.index)}
              >
                <span className="flex-1 min-w-0">
                  <span className="fcal__weekname block">Week {idx + 1}</span>
                  <span className="fcal__weekrange block">{shortDay(from)} – {shortDay(to)} {view.year}</span>
                  <span className="fcal__weekfob block" title={formatCurrency(week.totals.fob)}>
                    {formatShortCurrency(week.totals.fob)}
                  </span>
                  <span className="fcal__weekcount block">{plural(week.totals.count, 'shipment')}</span>
                </span>
                <Spark values={bars} colour={colour} />
                <ChevronRight size={15} className="text-slate-300 flex-shrink-0" />
              </button>
            );
          })}
        </div>
      </div>

      {/* ── month summary ───────────────────────────────────────────────── */}
      <div className="fcal__summary">
        <div className="flex items-center gap-2.5 pr-2">
          <span className="fcal__sumicon" style={{ background: '#eaf1ff', color: '#1d4ed8' }}><BarChart3 size={17} /></span>
          <span>
            <span className="fcal__sumvalue block">Month Summary</span>
            <span className="fcal__sumlabel block">{MONTHS[view.monthIndex]} {view.year}</span>
          </span>
        </div>

        <SummaryCard icon={IndianRupee} tint={{ bg: '#eaf1ff', fg: '#1d4ed8' }} label="Total FOB"
          value={formatShortCurrency(totals.fob)} title={formatCurrency(totals.fob)} delta={12} />
        <SummaryCard icon={Package} tint={{ bg: '#e9f9f0', fg: '#047857' }} label="Shipments"
          value={totals.count} delta={8} />
        <SummaryCard icon={Users} tint={{ bg: '#f5efff', fg: '#7c3aed' }} label="Clients"
          value={clients} delta={5} />
        <SummaryCard icon={BarChart3} tint={{ bg: '#fff4e6', fg: '#b45309' }} label="Avg. FOB / shipment"
          value={formatShortCurrency(avgPerShipment)} title={formatCurrency(avgPerShipment)} delta={6} />
      </div>
    </div>
  );
};

export default FobCalendar;
